/**
 * ActivityFeed — pulls /api/user/activity-feed and renders a timestamped stream
 * of the current user's recent actions (lesson completions, badges, saved batches).
 *
 * Uses HomeLyph Pro tokens. Silent when the user has zero events.
 */
import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { CheckCircle2, Award, ClipboardList, Activity, Users } from 'lucide-react';

const ICONS = {
  'check-circle': CheckCircle2,
  'award': Award,
  'clipboard-list': ClipboardList,
  'users': Users,
};

const relTime = (iso) => {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (isNaN(t)) return '';
  const diff = (Date.now() - t) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
};

const ActivityFeed = ({ limit = 8, allowCommunityToggle = false }) => {
  const { API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [events, setEvents] = useState(null);
  const [mode, setMode] = useState('self'); // 'self' | 'community'

  useEffect(() => {
    let alive = true;
    setEvents(null);
    const url = `${API}/user/activity-feed?limit=${limit}${mode === 'community' ? '&community=true' : ''}`;
    axios.get(url)
      .then(r => { if (alive) setEvents(r.data?.events || []); })
      .catch(() => { if (alive) setEvents([]); });
    return () => { alive = false; };
  }, [API, limit, mode]);

  if (events === null) return null;

  return (
    <Card className="pro-card border-0" data-testid="activity-feed">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="font-heading text-base font-extrabold flex items-center gap-2">
            <Activity className="w-4 h-4 text-primary" /> Recent activity
          </CardTitle>
          {allowCommunityToggle && (
            <div className="inline-flex items-center rounded-full bg-muted/70 p-0.5 text-[11px] font-semibold" data-testid="activity-feed-mode-toggle">
              <button
                type="button"
                onClick={() => setMode('self')}
                className={`px-3 py-1 rounded-full transition-colors ${mode === 'self' ? 'bg-primary text-primary-foreground shadow' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid="activity-feed-tab-you"
              >
                You
              </button>
              <button
                type="button"
                onClick={() => setMode('community')}
                className={`px-3 py-1 rounded-full transition-colors ${mode === 'community' ? 'bg-primary text-primary-foreground shadow' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid="activity-feed-tab-community"
              >
                Community
              </button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-2">
        {events.length === 0 && (
          <p className="text-sm text-muted-foreground italic">
            {mode === 'community'
              ? 'The community feed is quiet right now — check back once other members finish lessons or earn badges.'
              : 'Once you complete a lesson, save a batch or earn a badge it\u2019ll show up here.'}
          </p>
        )}
        <ul className="space-y-2">
          {events.map((e, i) => {
            const Icon = ICONS[e.icon] || Activity;
            return (
              <li key={i} data-testid={`activity-item-${i}`}>
                <button
                  type="button"
                  onClick={() => e.link && navigate(e.link)}
                  className="w-full flex items-start gap-3 rounded-xl px-3 py-2 text-left hover:bg-muted/60 transition-colors"
                >
                  <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-foreground truncate">{e.title}</div>
                    <div className="text-[11px] text-muted-foreground">{relTime(e.at)}</div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
};

export default ActivityFeed;
